import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

interface SignupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function SignupDialog({ open, onOpenChange }: SignupDialogProps) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;

  const handleEmailSubmit = async () => {
    if (!email) return;

    setIsLoading(true);
    setEmailError("");
    try {
      const response = await fetch("/check_email_registration", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (data.result == false) {
        setEmailError("Email déjà utilisé");
        setIsLoading(false);
      } else {
        window.location.href = "/users/sign_up?email=" + email;
      }
    } catch (error) {
      window.location.href = "/users/sign_up?email=" + email;
    }
  };

  const handleSignup = async () => {
    if (!firstName || !lastName || !password) return;

    setIsLoading(true);
    try {
      const response = await fetch("/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          password,
        }),
      });

      if (response.ok) {
        console.log("User created successfully");
        onOpenChange(false);
        // Reset form
        setStep(1);
        setEmail("");
        setFirstName("");
        setLastName("");
        setPassword("");
      } else {
        console.error("Signup failed");
      }
    } catch (error) {
      console.error("Signup error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    console.log("Google signup attempt");
  };

  const resetForm = () => {
    setStep(1);
    setEmail("");
    setFirstName("");
    setLastName("");
    setPassword("");
    setEmailError("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) resetForm();
      }}
    >
      <DialogContent className="sm:max-w-md animate-scale-in">
        <DialogHeader className="text-center space-y-2">
          <DialogTitle className="text-2xl font-display text-center">Rejoignez Wememberz</DialogTitle>
          <DialogDescription className="text-muted-foreground text-center">
            {step === 1 ? "Créez votre compte et commencez à immortaliser vos souvenirs" : "Complétez votre profil"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {step === 1 ? (
            <>
              {/* Étape 1: Email */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-center block">Adresse email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="transition-smooth focus:ring-2 focus:ring-[#f7bbac]/20 focus:border-[#f7bbac]"
                  />
                </div>

                <Button
                  onClick={handleEmailSubmit}
                  variant="rainbow"
                  size="lg"
                  className="w-full font-medium"
                  disabled={isLoading || !email}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Vérification...
                    </div>
                  ) : (
                    "Continuer"
                  )}
                </Button>

                {emailError && <div className="text-sm text-red-500 text-center mt-2">{emailError}</div>}
              </div>

              {/* Séparateur */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground text-center">ou continuer avec</span>
                </div>
              </div>

              {/* Bouton Google */}
              <form method="POST" action="/users/auth/google_oauth2">
                <input type="hidden" name="authenticity_token" value={csrf} />
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-smooth focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-11 px-8"
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  S'inscrire avec Google
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Étape 2: Informations complètes */}
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground text-center">
                  Email: <span className="font-medium">{email}</span>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-center block">Prénom</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Votre prénom"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="transition-smooth focus:ring-2 focus:ring-[#f7bbac]/20 focus:border-[#f7bbac]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-center block">Nom</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Votre nom"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="transition-smooth focus:ring-2 focus:ring-[#f7bbac]/20 focus:border-[#f7bbac]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-center block">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="transition-smooth focus:ring-2 focus:ring-[#f7bbac]/20 focus:border-[#f7bbac]"
                  />
                </div>

                <div className="flex gap-3">
                  <Button onClick={() => setStep(1)} variant="outline" size="lg" className="flex-1 font-medium">
                    Retour
                  </Button>
                  <Button
                    onClick={handleSignup}
                    variant="rainbow"
                    size="lg"
                    className="flex-1 font-medium"
                    disabled={isLoading || !firstName || !lastName || !password}
                  >
                    {isLoading ? "Création..." : "S'inscrire"}
                  </Button>
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              {/* Avantages de l'inscription */}
              <div className="space-y-4 pt-2">
                <h4 className="text-sm font-semibold text-center text-muted-foreground">Pourquoi s'inscrire ?</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-3 text-sm">
                    <div className="w-2 h-2 bg-[#f7bbac] rounded-full flex-shrink-0"></div>
                    <span className="text-muted-foreground">Synchronisation sur tous vos appareils</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3 text-sm">
                    <div className="w-2 h-2 bg-[#f7bbac] rounded-full flex-shrink-0"></div>
                    <span className="text-muted-foreground">Sauvegarde automatique de vos créations</span>
                  </div>
                </div>
              </div>

              {/* Lien connexion */}
              <div className="text-center text-sm text-muted-foreground">
                Déjà un compte ?{" "}
                <button
                  onClick={() => console.log("Login clicked")}
                  className="text-[#f7bbac] hover:text-[#f7bbac]/80 transition-smooth underline-offset-4 hover:underline font-medium"
                >
                  Se connecter
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
